/**
 * Roles that get the platform's ad-free perk and nothing else — no quota
 * boost, no fee waiver, no Goon+/Goon Max feature access. Per product
 * decision 2026-09-20: being staff isn't a subscription tier; a moderator,
 * dev, or admin who wants Goon+ perks pays for Goon+ like anyone else. This
 * is deliberately a wider set than isAdminRole (admin/super_admin) — dev and
 * moderator get the same ad-free treatment, they just don't have the admin
 * role's other panel/moderation capabilities.
 */
export function isAdFreeStaffRole(role: string | null | undefined): boolean {
  return role === "admin" || role === "dev" || role === "moderator" || role === "super_admin"
}

/**
 * Single source of truth for "has this user paid for a premium experience?"
 *
 * This existed in three different shapes before, and they disagreed:
 *   - /api/ads/serve       required subscription_status === "active" AND tier !== "free"
 *   - /api/ads/video-ad    checked tier !== "free" only, ignoring status
 *   - popunder-injector    read the raw tier client-side
 *
 * So the same user could be ad-free in one surface and see ads in another. Worse,
 * every variant missed the users who paid *without* an active Stripe subscription:
 *
 *   - Founders     one-time purchase, so subscription_status is never "active"
 *   - Lifetime     same — paid up front, no recurring subscription
 *   - Trialing     Stripe reports "trialing", not "active", during a trial
 *
 * All three would have been shown ads despite paying. Admins were also missed,
 * which is why testing premium behaviour meant switching your own tier by hand.
 *
 * (There is no "Lifetime" purchase path — that was leftover pre-Founder scaffolding,
 * never sold to a real customer, and has been removed entirely.)
 */

export interface EntitlementProfile {
  subscription_tier?: string | null
  subscription_status?: string | null
  is_founder?: boolean | null
  role?: string | null
  showcase_pro_access?: boolean | null
}

// Statuses that still represent a user in good standing. "past_due" is
// deliberately included: the payment is late, not failed, and yanking paid perks
// mid-grace-period punishes people for a card that expired. Stripe moves them to
// "canceled"/"unpaid" once the retry window is exhausted, which does drop perks.
// "canceling" is OUR status (webhooks/stripe/route.ts writes it when
// cancel_at_period_end is set): the user turned off auto-renew but has paid
// through the end of the period, so they keep everything until Stripe sends
// customer.subscription.deleted at period end, which is what drops them to free.
// "cancel_at_period_end" is an older spelling of the same state, still written by
// the legacy /api/stripe/cancel-subscription route.
const ACTIVE_SUBSCRIPTION_STATUSES = new Set(["active", "trialing", "past_due", "canceling", "cancel_at_period_end"])

export function hasActiveSubscriptionStatus(status: string | null | undefined): boolean {
  return ACTIVE_SUBSCRIPTION_STATUSES.has(status ?? "")
}

/**
 * True when the user should get the paid experience: no ads, premium sources, etc.
 */
export function hasPremiumAccess(profile: EntitlementProfile | null | undefined): boolean {
  if (!profile) return false

  // Staff (admin/dev/moderator/super_admin) get the platform's ad-free perk —
  // see isAdFreeStaffRole. This also means premium ad behaviour can be
  // verified without anyone having to edit their own subscription_tier to
  // test it.
  if (isAdFreeStaffRole(profile.role)) return true

  // Permanent, one-time purchase. Carries no live Stripe subscription, so it
  // must be checked before any subscription_status test.
  if (profile.is_founder) return true

  const tier = profile.subscription_tier
  if (!tier || tier === "free") return false

  // A tier with no status at all is treated as unpaid — the tier column alone
  // isn't proof of payment, since it lingers after some cancellation paths.
  return hasActiveSubscriptionStatus(profile.subscription_status)
}

/**
 * The tier label every label-based helper (isGoonPlusTier, getStoreBlockLimit,
 * isPremiumTier, ...) should be fed instead of the raw subscription_tier column,
 * which lingers after cancellation and says nothing about founders. Founders keep
 * Goon Max perks forever; everyone else — including staff — only counts while
 * their own subscription is in good standing. Being staff is not a paid tier
 * (see isAdFreeStaffRole): an admin/dev/moderator with no subscription of
 * their own resolves to "free" here, same as any other unsubscribed user.
 */
export function entitledTierLabel(profile: EntitlementProfile | null | undefined): string {
  if (!profile) return "free"
  if (profile.is_founder) return "founder"
  const tier = profile.subscription_tier
  if (!tier || tier === "free") return "free"
  return hasActiveSubscriptionStatus(profile.subscription_status) ? tier : "free"
}

/**
 * Owner showcase switch (profiles.showcase_pro_access, flipped in Admin Settings).
 * Lets the platform account USE every tier-gated feature so both the free and the
 * Pro experience can be shown, without taking a paid tier. It is honoured only for
 * role = super_admin, so the flag does nothing on any other account.
 */
export function hasShowcaseProAccess(profile: EntitlementProfile | null | undefined): boolean {
  return !!profile && profile.role === "super_admin" && profile.showcase_pro_access === true
}

/**
 * Tier label for ACCESS gates only ("can this account open/use this feature?"):
 * Custom Page, auto-portfolio sync, custom licenses, the commission widget,
 * automatic backup sync, premium AI models. Same as entitledTierLabel, except the
 * showcase switch counts as top tier.
 *
 * Never use this for quotas, limits, fees, revenue share or ad-free: those are
 * perks and must keep following the account's real tier (entitledTierLabel), so
 * the owner still experiences the real limits.
 */
export function accessTierLabel(profile: EntitlementProfile | null | undefined): string {
  return hasShowcaseProAccess(profile) ? "goon_max" : entitledTierLabel(profile)
}

/** Columns any server-side caller must select for hasPremiumAccess to be correct. */
export const ENTITLEMENT_COLUMNS =
  "subscription_tier, subscription_status, is_founder, role, showcase_pro_access"
